import { useCallback, useEffect, useRef, useState } from 'react';
import { contentRect, toClient, toNormalized, type ContentRect, type NormPoint } from './roiGeometry';
import {
  clearCalibration, fetchCalibration, saveCalibration,
  type Correspondence,
} from './geolocate';

/**
 * Tells the platform where this camera's picture lands on the ground.
 *
 * The operator clicks a recognisable ground feature in the picture, then the same feature
 * on the map, four or more times. Those pairs determine the whole image-to-ground mapping
 * (a homography) — no compass, mount height or lens FOV required. See
 * backend/camera_calibration.py.
 *
 * Two details that are not decoration:
 *
 *   * It calibrates against a FROZEN STILL, not the live feed. Matching a feature between a
 *     moving picture and a map is hopeless, and a pair clicked two frames apart is a pair
 *     clicked wrong.
 *
 *   * The residual for every pair is shown after saving. A mis-clicked pair is the failure
 *     mode here, and it is invisible unless you print the number — the fit still succeeds,
 *     it is just quietly wrong. Anything over OUTLIER_RESIDUAL_M is flagged red.
 */

const FALLBACK_FRAME = { width: 640, height: 360 };
const MIN_PAIRS = 4;
const MAX_PAIRS = 32;

export interface CalibrationEditorProps {
  /** The `.video-wrapper` the video and this overlay share. */
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  apiUrl: string;
  authHeaders: (json?: boolean) => HeadersInit;
  onClose: () => void;
  /** Told whether this feed is now calibrated, so the panel can light its button. */
  onCalibrationChange?: (calibrated: boolean) => void;
  /**
   * Hands the map a callback to run on its next click, or `null` to stand down. The map
   * lives in App.tsx and this component lives inside the video panel, so the pick has to be
   * requested rather than listened for.
   */
  onRequestGroundPick: (handler: ((p: { lat: number; lng: number }) => void) | null) => void;
}

export default function CalibrationEditor({
  wrapperRef, apiUrl, authHeaders, onClose, onCalibrationChange, onRequestGroundPick,
}: CalibrationEditorProps) {
  const [pairs, setPairs] = useState<Correspondence[]>([]);
  const [pending, setPending] = useState<NormPoint | null>(null);
  const [rect, setRect] = useState<ContentRect>({ left: 0, top: 0, width: 0, height: 0 });
  const [still, setStill] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [rms, setRms] = useState<number | null>(null);
  const [residuals, setResiduals] = useState<number[]>([]);
  const [outliers, setOutliers] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const svgRef = useRef<SVGSVGElement | null>(null);

  // --- keep the overlay glued to the picture, not to the element -------- //
  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const img = wrapper.querySelector('img.video-stream') as HTMLImageElement | null;
    setRect(contentRect(
      wrapper.clientWidth, wrapper.clientHeight,
      img?.naturalWidth || FALLBACK_FRAME.width,
      img?.naturalHeight || FALLBACK_FRAME.height,
    ));
  }, [wrapperRef]);

  useEffect(() => {
    measure();
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(measure);
    ro.observe(wrapper);
    const poll = setInterval(measure, 1000);
    return () => { ro.disconnect(); clearInterval(poll); };
  }, [measure, wrapperRef]);

  // --- the frozen still -------------------------------------------------- //
  const grabStill = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/drone/frame.jpg`, { headers: authHeaders() });
      if (!res.ok) {
        setStatus(res.status === 404
          ? 'No frame on this feed yet — wait for the video to start'
          : `Could not grab a still (${res.status})`);
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      setStill(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
      setStatus(null);
    } catch {
      setStatus('Could not reach the backend');
    }
  }, [apiUrl, authHeaders]);

  useEffect(() => { grabStill(); }, [grabStill]);
  // Revoke on unmount only — grabStill already revokes the one it replaces.
  const stillRef = useRef<string | null>(null);
  stillRef.current = still;
  useEffect(() => () => { if (stillRef.current) URL.revokeObjectURL(stillRef.current); }, []);

  // --- load whatever this feed already has ------------------------------- //
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cal = await fetchCalibration(apiUrl, authHeaders);
        if (!cancelled && cal?.correspondences?.length) {
          setPairs(cal.correspondences);
          setRms(cal.rms_error_m ?? null);
        }
      } catch { /* a missing calibration is the normal case, not an error */ }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, authHeaders]);

  // --- pairing ----------------------------------------------------------- //
  const cancelPick = useCallback(() => {
    setPending(null);
    onRequestGroundPick(null);
  }, [onRequestGroundPick]);

  // Never leave the map in pick mode because this panel closed.
  useEffect(() => () => { onRequestGroundPick(null); }, [onRequestGroundPick]);

  const handleImageClick = (e: React.MouseEvent) => {
    if (pairs.length >= MAX_PAIRS) {
      setStatus(`At most ${MAX_PAIRS} calibration points`);
      return;
    }
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const [u, v] = toNormalized(e.clientX - box.left, e.clientY - box.top, rect);
    setPending([u, v]);
    setStatus(null);
    onRequestGroundPick(({ lat, lng }) => {
      setPairs(prev => [...prev, { u, v, lat, lng }]);
      // A new pair invalidates the fit these numbers describe — clear rather than mislead.
      setRms(null); setResiduals([]); setOutliers([]);
      setPending(null);
      onRequestGroundPick(null);
    });
  };

  const removePair = (index: number) => {
    setPairs(prev => prev.filter((_, i) => i !== index));
    setRms(null); setResiduals([]); setOutliers([]);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (pending) cancelPick(); else onClose(); }
      if (e.key === 'Backspace' && !pending && pairs.length) removePair(pairs.length - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, pairs.length, cancelPick, onClose]);

  // --- persistence -------------------------------------------------------- //
  const save = async () => {
    if (pairs.length < MIN_PAIRS) return;
    setBusy(true); setStatus(null);
    try {
      const result = await saveCalibration(apiUrl, authHeaders, pairs);
      setRms(result.calibration.rms_error_m ?? null);
      setResiduals(result.residuals_m || []);
      setOutliers(result.outliers || []);
      onCalibrationChange?.(true);
      if (result.outliers?.length) {
        setStatus(`Saved, but ${result.outliers.length} point(s) do not agree with the rest — `
          + 'check the ones marked red, then re-save.');
      }
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await clearCalibration(apiUrl, authHeaders);
      setPairs([]); setRms(null); setResiduals([]); setOutliers([]); setStatus(null);
      onCalibrationChange?.(false);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Clear failed');
    } finally {
      setBusy(false);
    }
  };

  // --- render -------------------------------------------------------------- //
  const remaining = MIN_PAIRS - pairs.length;
  const btn = (extra?: React.CSSProperties): React.CSSProperties => ({
    background: 'rgba(0,0,0,0.6)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)',
    borderRadius: 3, padding: '3px 9px', fontSize: 11, cursor: 'pointer',
    letterSpacing: '0.04em', ...extra,
  });

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 6 }}>
      {/* The frozen still sits over the live feed: a pair clicked across two frames is a
          pair clicked wrong. */}
      {still && (
        <img
          src={still} alt="" draggable={false}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'contain', pointerEvents: 'none', background: '#000',
          }}
        />
      )}

      <svg
        ref={svgRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          cursor: pending ? 'progress' : 'crosshair',
        }}
        onClick={handleImageClick}
      >
        {pairs.map((p, i) => {
          const [x, y] = toClient(p.u, p.v, rect);
          const bad = outliers.includes(i);
          const colour = bad ? '#ff5252' : '#7cf59a';
          return (
            <g key={i} onClick={(e) => { e.stopPropagation(); removePair(i); }}
               style={{ cursor: 'pointer' }}>
              <circle cx={x} cy={y} r={7} fill="rgba(0,0,0,0.55)" stroke={colour} strokeWidth={2} />
              <line x1={x - 11} y1={y} x2={x + 11} y2={y} stroke={colour} strokeWidth={1} />
              <line x1={x} y1={y - 11} x2={x} y2={y + 11} stroke={colour} strokeWidth={1} />
              <text x={x + 12} y={y - 8} fill={colour} fontSize={11} style={{ userSelect: 'none' }}>
                {i + 1}{residuals[i] !== undefined ? ` · ${residuals[i].toFixed(1)} m` : ''}
              </text>
            </g>
          );
        })}

        {pending && (() => {
          const [x, y] = toClient(pending[0], pending[1], rect);
          return (
            <g pointerEvents="none">
              <circle cx={x} cy={y} r={9} fill="none" stroke="#ffd166" strokeWidth={2}
                      strokeDasharray="4 3" />
              <circle cx={x} cy={y} r={2.5} fill="#ffd166" />
            </g>
          );
        })()}
      </svg>

      <div style={{
        position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6,
        alignItems: 'center', flexWrap: 'wrap', maxWidth: 'calc(100% - 16px)',
      }}>
        <span style={{
          background: 'rgba(0,0,0,0.72)', color: pending ? 'var(--color-warning)' : 'var(--color-accent)', fontSize: 11,
          padding: '3px 8px', borderRadius: 3, letterSpacing: '0.05em',
        }}>
          {pending
            ? 'NOW CLICK THAT SAME SPOT ON THE MAP'
            : remaining > 0
              ? `CALIBRATE — click a ground feature, then it on the map (${remaining} more)`
              : 'CALIBRATE — add more pairs for a tighter fit, or save'}
        </span>

        {rms !== null && (
          <span style={{
            background: 'rgba(0,0,0,0.72)', fontSize: 11, padding: '3px 8px', borderRadius: 3,
            color: rms <= 1 ? 'var(--color-accent)' : rms <= 3 ? 'var(--color-warning)' : '#ff5252',
          }}>
            FIT ±{rms.toFixed(2)} m
          </span>
        )}

        {pending && <button style={btn()} onClick={cancelPick}>CANCEL POINT</button>}
        {!pending && pairs.length >= MIN_PAIRS && (
          <button style={btn({ borderColor: '#7cf59a', color: '#7cf59a' })}
                  onClick={save} disabled={busy}>
            {busy ? 'SAVING…' : 'SAVE'}
          </button>
        )}
        <button style={btn()} onClick={grabStill} disabled={busy}>NEW STILL</button>
        <button style={btn()} onClick={clear} disabled={busy}>CLEAR</button>
        <button style={btn()} onClick={onClose}>CLOSE</button>

        {status && (
          <span style={{
            background: 'rgba(120,0,0,0.78)', color: '#ffd7d7', fontSize: 11,
            padding: '3px 8px', borderRadius: 3, maxWidth: 360,
          }}>{status}</span>
        )}
      </div>
    </div>
  );
}
